import { Link } from "react-router-dom";
import { Avatar } from "@/components/shared/Avatar";
import CustomerLogo from "@/components/customers/CustomerLogo";
import CustomerStatusPill from "@/components/customers/CustomerStatusPill";
import { activityTypeLabel } from "@/lib/customer-activity-calc";
import { describeDue, formatDaysAgo, formatMunicipality, type CustomerListRow } from "@/lib/customer-list-calc";
import { FOLLOW_UP_CHANNEL_LABELS } from "@/lib/follow-up-labels";
import { countryName, isForeignCountry } from "@/lib/countries";
import { formatDateShort, formatKrCompact } from "@/lib/format";
import { cn } from "@/lib/utils";

const MAX_OWNER_AVATARS = 3;
const DASH = "–";

interface CustomerListRowItemProps {
  row: CustomerListRow;
  onClaimOwnership: (customerId: string) => void;
}

function Sub({ children, warn = false }: { children: React.ReactNode; warn?: boolean }) {
  return <div className={cn("mt-0.5 text-xs", warn ? "font-medium text-destructive" : "text-muted-foreground")}>{children}</div>;
}

function count(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** Én kunde i kundeliste v2: samme tall som kundekortet, i én rad. */
export default function CustomerListRowItem({ row, onClaimOwnership }: CustomerListRowItemProps) {
  const place = [row.industry, formatMunicipality(row.municipality)].filter(Boolean).join(" · ");
  const step = row.nextStep;
  const stepMeta = step
    ? [describeDue(step), step.channel ? FOLLOW_UP_CHANNEL_LABELS[step.channel].toLowerCase() : null, step.source === "rule" ? "fra regel" : null]
        .filter(Boolean)
        .join(" · ")
    : null;
  const inPlay = [
    row.pipeOfferCount > 0 ? count(row.pipeOfferCount, "tilbud", "tilbud") : null,
    row.openProcessCount > 0 ? count(row.openProcessCount, "prosess", "prosesser") : null,
  ].filter(Boolean).join(" · ");

  return (
    <tr className="border-b border-border/50 last:border-0 hover:bg-muted/30">
      <td className="py-2.5 px-3">
        <div className="flex items-center gap-3">
          <CustomerLogo name={row.name} logoUrl={row.logoUrl} className="h-9 w-9 text-base" />
          <div className="min-w-0">
            <Link to={`/kunde/${row.id}`} className="font-medium text-primary hover:underline">{row.name}</Link>
            {isForeignCountry(row.countryCode) && (
              <span className="ml-2 text-xs text-muted-foreground">{countryName(row.countryCode)}</span>
            )}
            {place && <Sub>{place}</Sub>}
          </div>
        </div>
      </td>
      <td className="py-2.5 px-3">
        {row.status ? <CustomerStatusPill status={row.status} /> : <span className="text-muted-foreground">{DASH}</span>}
        {row.isOverridden ? (
          <Sub>overstyrt</Sub>
        ) : row.status === "warm" && row.coolsAt ? (
          <Sub warn={row.group === "cooling"}>
            {row.coolsInDays != null && row.coolsInDays >= 0 && row.coolsInDays <= 30
              ? `kjølner om ${count(row.coolsInDays, "dag", "dager")}`
              : `kjølner ${formatDateShort(row.coolsAt)}`}
          </Sub>
        ) : null}
      </td>
      <td className="py-2.5 px-3">
        {row.owners.length === 0 ? (
          <span className="text-xs text-muted-foreground">
            ingen ·{" "}
            <button type="button" className="text-primary hover:underline" onClick={() => onClaimOwnership(row.id)}>
              ta eierskap
            </button>
          </span>
        ) : (
          <ul className="flex items-center" aria-label="Eiere">
            {row.owners.slice(0, MAX_OWNER_AVATARS).map((o, i) => (
              <li key={o.userId} title={o.name} className={cn(i > 0 && "-ml-2")}>
                <Avatar name={o.name} avatarUrl={o.avatarUrl} size="sm" className="ring-2 ring-background" />
              </li>
            ))}
            {row.owners.length > MAX_OWNER_AVATARS && (
              <li className="ml-1 text-xs text-muted-foreground">+{row.owners.length - MAX_OWNER_AVATARS}</li>
            )}
          </ul>
        )}
      </td>
      <td className="py-2.5 px-3 min-w-[14rem]">
        {step ? (
          <>
            <div className="font-medium">{step.title}</div>
            <Sub warn={row.group === "overdue"}>{stepMeta}</Sub>
          </>
        ) : (
          <>
            <div className="text-sm text-muted-foreground">Ingen neste steg</div>
            <Sub>
              <Link to={`/kunde/${row.id}`} className="text-primary hover:underline">Ny handling</Link>
            </Sub>
          </>
        )}
      </td>
      <td className="py-2.5 px-3 text-right tabular-nums">
        <div className="font-medium">{formatKrCompact(row.pipeKr)}</div>
        <Sub>{inPlay || "ingenting i spill"}</Sub>
      </td>
      <td className="py-2.5 px-3 text-right tabular-nums">
        <div className="font-medium">{row.deliveredKr != null ? formatKrCompact(row.deliveredKr) : DASH}</div>
        <Sub>{row.deliveredCount > 0 ? count(row.deliveredCount, "prosjekt", "prosjekter") : "ingen avsluttede siste 12 mnd"}</Sub>
      </td>
      <td className="py-2.5 px-3">
        {row.lastContact ? (
          <>
            <div className="font-medium">{formatDaysAgo(row.lastContact.daysAgo)}</div>
            <Sub>{[activityTypeLabel(row.lastContact.type).toLowerCase(), row.lastContact.userName?.split(" ")[0]].filter(Boolean).join(" · ")}</Sub>
          </>
        ) : (
          <>
            <div className="text-muted-foreground">{DASH}</div>
            <Sub>aldri logget</Sub>
          </>
        )}
      </td>
    </tr>
  );
}
